import { Vector } from '@ikegps/core-measurement';
import { MarkerType, type Point } from '@ikegps/core-api';
import type { SVGRenderOptions } from '../../../draw';
import {
	type ApprovableInsightDrawable,
	type CollectionModelDrawable,
	type DrawableWithLabel,
	MoveableDrawable,
} from './drawable';
import type { CollectionModelDrawableState } from './drawable.state';
import { CollectionModelDrawableLabel } from './label.drawable';

/**
 * MarkerOffset drawable has its position set dynamically using to the Base Marker's "offset" property.
 * Technically it has no (x,y) state since that is computed from the capture and the Base Marker.
 */
export class CollectionModelDrawableMarkerOffset extends MoveableDrawable implements DrawableWithLabel {
	static createWithLabel(drawableState: CollectionModelDrawableState, labelPoint?: Point) {
		const marker = new CollectionModelDrawableMarkerOffset(drawableState);

		if (!labelPoint) {
			labelPoint = {
				x: 0,
				y: marker.y,
			};
		}

		const label = new CollectionModelDrawableLabel(
			drawableState,
			marker.labelId,
			labelPoint.x,
			labelPoint.y,
			marker.id
		);

		return { marker, label };
	}

	constructor(
		collectionModelDrawableState: CollectionModelDrawableState,
		id: string = CollectionModelDrawableMarkerOffset.DEFAULT_DRAWABLE_ID,
		labelId: string = CollectionModelDrawableMarkerOffset.DEFAULT_LABEL_ID
	) {
		super(collectionModelDrawableState, id);
		this._labelId = labelId;
	}

	static DEFAULT_DRAWABLE_ID = 'offset';
	static DEFAULT_LABEL_ID = 'offset:label';

	static isBaseOffset(t: CollectionModelDrawable): t is CollectionModelDrawableMarkerOffset {
		return t instanceof CollectionModelDrawableMarkerOffset;
	}

	private _labelId: string;

	get labelId() {
		return this._labelId;
	}

	get label(): CollectionModelDrawableLabel {
		return this.drawableState.getDrawable<CollectionModelDrawableLabel>(this.labelId);
	}

	getLabelConnectionPoint(options: SVGRenderOptions): Point {
		return {
			x: this.label.isOnLeftSide()
				? this.x - options.draw.state.markerOffsetWidth // Left
				: this.x + options.draw.state.markerOffsetWidth, // Right
			y: this.y,
		};
	}

	override get x() {
		if (this.drawableState.capture == null) {
			return 0;
		}
		return this.drawableState.capture.resolution.width / 2;
	}

	override get y() {
		const base = this.drawableState.base;
		if (base == null || this.drawableState.capture == null) {
			return 0;
		}
		const baseXY = base.getVector();
		const moveAmount = new Vector(0, base.offset ?? 0);
		return this.drawableState.collection.state.getGeometry(this.captureId).movePoint(baseXY, moveAmount).y;
	}

	// The offset marker position is relative to the Base marker,
	// so adjust the move location of the Base marker by applying a negative base offset.
	override move(x: number, y: number): void {
		if (!this.drawableState.base || this.drawableState.capture == null) {
			return;
		}
		const clickPoint = new Vector(x, y);
		const offset = new Vector(0, -1 * (this.drawableState.base.offset ?? 0));
		const offsetY = this.drawableState.collection.state.getGeometry(this.captureId).movePoint(clickPoint, offset).y;
		this.drawableState.base.move(x, offsetY);
	}

	get type() {
		return MarkerType.BASE_OFFSET;
	}

	override get isDeletable() {
		return false;
	}

	override get isBaseOffset(): boolean {
		return true;
	}

	override remove() {
		super.remove();
		// Remove this offset marker's label too, unless it has already been removed
		if (this.drawableState.hasDrawable(this.labelId)) {
			this.label.remove();
		}
	}

	override get isInsightMeasurement(): boolean {
		return false;
	}

	override isApprovableInsightDrawable(): this is ApprovableInsightDrawable {
		return false;
	}

	override get isVisible(): boolean {
		return true;
	}

	override clone(newParentDrawableState: CollectionModelDrawableState): CollectionModelDrawableMarkerOffset {
		if (!newParentDrawableState.hasDrawable(this.labelId)) {
			this.label.clone(newParentDrawableState);
		}
		return new CollectionModelDrawableMarkerOffset(newParentDrawableState, this.id, this._labelId);
	}

	override equals(other: CollectionModelDrawable): boolean {
		return other instanceof CollectionModelDrawableMarkerOffset && this._equals(other);
	}
}
